/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description PaymentsTableActions
 */
import _ from 'lodash';
import { Dropdown, Tooltip, ButtonProps } from 'antd';
import { MoreOne } from '@icon-park/react';
import { MenuItemType } from 'antd/es/menu/interface';
import Button from 'components/widgets/button';
import { defaultDropdownButtonProps } from './ClaimTableActions';

interface IProps {
	id: string;
	fhirResource: Fhir.PaymentReconciliation;
	parsedAllocationData: PatientPayment.IParsedPaymentReconciliation;
	allocationStarted?: boolean;
	allocationInProgress?: boolean;
	redirectAfterSave?: boolean;
	refetchPaymentListOnSave?: boolean;
}

const PaymentsTableActions: React.FC<IProps & ButtonProps> = ({
	id,
	fhirResource,
	parsedAllocationData,
	allocationStarted = false,
	allocationInProgress = false,
	redirectAfterSave = true,
	refetchPaymentListOnSave = true,
	size,
}) => {
	const allocationStatus = parsedAllocationData?.outcome;
	const hasRecordedAllocations = !_.isEmpty(parsedAllocationData?.allocations);
	const isLockedByStatus = allocationStatus === 'complete' || allocationStatus === 'partial';

	const shouldAllowEditRequest = allocationStarted ? !allocationInProgress : !isLockedByStatus && !hasRecordedAllocations;

	const shouldAllowDeallocate = isLockedByStatus;
	const shouldAllowDelete = !hasRecordedAllocations && (_.isNil(allocationStatus) || allocationStatus === 'queued');
	const refundAmount = parsedAllocationData?.refundAmount || 0;

	let editTooltip = '';
	if (allocationInProgress) {
		editTooltip = 'Finish or cancel the current allocation to edit payment details.';
	} else if (!shouldAllowEditRequest) {
		editTooltip = 'To edit the payment details, deallocate the payment first.';
	}

	const schema: MenuItemType[] = [
		{
			key: 'editPayment',
			className: 'tw-dropdown-menu-item',
			disabled: !shouldAllowEditRequest,
			label: (
				<Tooltip title={editTooltip}>
					<Button.EditPaymentDetails
						disabled={!shouldAllowEditRequest}
						fhirResource={fhirResource}
						parsedAllocationData={parsedAllocationData}
						redirectAfterSave={redirectAfterSave}
						refetchPaymentListOnSave={refetchPaymentListOnSave}
						{...defaultDropdownButtonProps}
					/>
				</Tooltip>
			),
		},
		shouldAllowDeallocate && {
			key: 'deallocatePayment',
			className: 'tw-dropdown-menu-item',
			danger: true,
			label: <Button.DeletePaymentRecord id={id} refundAmount={refundAmount} isDelete={false} {...defaultDropdownButtonProps} />,
		},
		shouldAllowDelete && {
			key: 'deletePayment',
			className: 'tw-dropdown-menu-item',
			danger: true,
			label: <Button.DeletePaymentRecord id={id} refundAmount={refundAmount} isDelete={true} {...defaultDropdownButtonProps} />,
		},
	];

	return (
		<Dropdown
			menu={{ items: schema }}
			trigger={['click']}
			overlayClassName='claim-actions-dropdown'
			disabled={!shouldAllowDelete && !shouldAllowDeallocate}
		>
			<Button color='default' size={size ?? 'small'} icon={<MoreOne size={16} />} onClick={(e) => e.stopPropagation()} />
		</Dropdown>
	);
};

export default PaymentsTableActions;
